import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/AuthContext';
import { useQuery } from '@tanstack/react-query';
import { Car, FileText, Bell, TrendingUp } from 'lucide-react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import type {
  Record as LogbookRecord,
  Reminder,
  Vehicle,
} from '@vehicle-logbook/shared';
import { formatDateShort } from '../../utils/dateFormat';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:5000/api';
const PREVIEW_LIMIT = 5;

export const Dashboard = () => {
  const { t, i18n } = useTranslation();
  const { user } = useAuth();

  const { data: stats, isLoading } = useQuery({
    queryKey: ['dashboard-stats'],
    queryFn: async () => {
      if (!user) return null;
      try {
        const token = await user.getIdToken();
        const [vehiclesRes, recordsRes, remindersRes] =
          await Promise.allSettled([
            axios.get(`${API_URL}/vehicles`, {
              headers: { Authorization: `Bearer ${token}` },
            }),
            axios.get(`${API_URL}/records`, {
              headers: { Authorization: `Bearer ${token}` },
              params: { limit: 1000, page: 1 },
            }),
            axios.get(`${API_URL}/reminders`, {
              headers: { Authorization: `Bearer ${token}` },
            }),
          ]);

        const vehicles =
          vehiclesRes.status === 'fulfilled'
            ? (vehiclesRes.value.data?.data as Vehicle[]) || []
            : [];
        const recordsData =
          recordsRes.status === 'fulfilled'
            ? recordsRes.value.data?.data
            : null;
        const records = (recordsData?.records as LogbookRecord[]) || [];
        const totalRecords = recordsData?.total || 0;
        const reminders =
          remindersRes.status === 'fulfilled'
            ? ((remindersRes.value.data?.data || []) as Reminder[])
            : [];

        const vehicleNameById = Object.fromEntries(
          vehicles.map((v) => [v.id, v.name])
        );

        return {
          totalVehicles: vehicles.length || 0,
          totalRecords: totalRecords,
          activeReminders: reminders.length,
          totalExpenses:
            records.reduce((sum, r) => sum + (r.cost || 0), 0) || 0,
          recentRecords: records.slice(0, PREVIEW_LIMIT),
          recentReminders: reminders.slice(0, PREVIEW_LIMIT),
          vehicleNameById,
        };
      } catch (error) {
        console.error('Failed to fetch dashboard stats:', error);
        return {
          totalVehicles: 0,
          totalRecords: 0,
          activeReminders: 0,
          totalExpenses: 0,
          recentRecords: [] as LogbookRecord[],
          recentReminders: [] as Reminder[],
          vehicleNameById: {} as Record<string, string>,
        };
      }
    },
    enabled: !!user,
  });

  const formatMoney = (amount: number, currency: string) => {
    const lang = i18n.language?.startsWith('uk') ? 'uk-UA' : 'en-US';
    return new Intl.NumberFormat(lang, {
      style: 'currency',
      currency: currency === 'UAH' ? 'UAH' : currency || 'UAH',
    }).format(amount);
  };

  const statCards = [
    {
      title: t('nav.vehicles'),
      value: stats?.totalVehicles || 0,
      icon: Car,
      color: 'bg-blue-500',
      link: '/vehicles',
    },
    {
      title: t('nav.records'),
      value: stats?.totalRecords || 0,
      icon: FileText,
      color: 'bg-green-500',
      link: '/records',
    },
    {
      title: t('nav.reminders'),
      value: stats?.activeReminders || 0,
      icon: Bell,
      color: 'bg-yellow-500',
      link: '/reminders',
    },
    {
      title: t('analytics.totalExpenses'),
      value: stats?.totalExpenses
        ? `₴${stats.totalExpenses.toLocaleString()}`
        : '₴0',
      icon: TrendingUp,
      color: 'bg-red-500',
      link: '/analytics',
    },
  ];

  if (!user) {
    return null;
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold text-gray-900">
          {t('nav.dashboard')}
        </h1>
        <p className="mt-2 text-sm text-gray-600">{user.email}</p>
      </div>

      <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
        {statCards.map((stat) => {
          const Icon = stat.icon;
          return (
            <Link
              key={stat.title}
              to={stat.link}
              className="relative overflow-hidden rounded-lg bg-white px-4 py-5 shadow sm:px-6 hover:shadow-md transition-shadow"
            >
              <dt>
                <div className={`absolute rounded-md ${stat.color} p-3`}>
                  <Icon className="h-6 w-6 text-white" />
                </div>
                <p className="ml-16 truncate text-sm font-medium text-gray-500">
                  {stat.title}
                </p>
              </dt>
              <dd className="ml-16 flex items-baseline">
                <p className="text-2xl font-semibold text-gray-900">
                  {isLoading ? '...' : stat.value}
                </p>
              </dd>
            </Link>
          );
        })}
      </div>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        <div className="bg-white shadow rounded-lg p-6">
          <h2 className="text-lg font-medium text-gray-900 mb-4">
            {t('nav.records')}
          </h2>
          {isLoading ? (
            <p className="text-sm text-gray-500">{t('common.loading')}</p>
          ) : stats && stats.recentRecords.length > 0 ? (
            <ul className="divide-y divide-gray-100">
              {stats.recentRecords.map((r) => (
                <li key={r.id} className="py-3 first:pt-0">
                  <p className="text-sm font-medium text-gray-900 truncate">
                    {r.title}
                  </p>
                  <p className="text-xs text-gray-500 mt-0.5">
                    {formatDateShort(r.date)} ·{' '}
                    {stats.vehicleNameById[r.vehicleId] ?? '—'} ·{' '}
                    {formatMoney(r.cost || 0, r.currency)}
                  </p>
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-sm text-gray-500">{t('record.noRecords')}</p>
          )}
        </div>
        <div className="bg-white shadow rounded-lg p-6">
          <h2 className="text-lg font-medium text-gray-900 mb-4">
            {t('nav.reminders')}
          </h2>
          {isLoading ? (
            <p className="text-sm text-gray-500">{t('common.loading')}</p>
          ) : stats && stats.recentReminders.length > 0 ? (
            <ul className="divide-y divide-gray-100">
              {stats.recentReminders.map((r) => (
                <li key={r.id} className="py-3 first:pt-0">
                  <p className="text-sm font-medium text-gray-900 truncate">
                    {r.title}
                  </p>
                  <p className="text-xs text-gray-500 mt-0.5">
                    {t('reminder.nextTrigger')}:{' '}
                    {r.nextTriggerDate
                      ? formatDateShort(r.nextTriggerDate)
                      : '—'}
                  </p>
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-sm text-gray-500">{t('reminder.noReminders')}</p>
          )}
        </div>
      </div>
    </div>
  );
};
